// ReportsPages.tsx — /liff/reports/site + /liff/reports/audit
//
// Both pages call report-api's /v1/reports list endpoint with different
// `scope` filters. Site = manager / tenant_admin; Audit = auditor /
// tenant_admin (scope=audit returns the read-only summarised set with PII
// redacted).

import React, { useEffect, useState } from "react";
import { useLiff } from "../../../liff/index.js";
import { fetchReportsList, type ReportsListData } from "../../../liff/api.js";
import {
  MobileShell, Card, CardHeader, MobileSpinner, EmptyMobile, ListRow,
  SecondaryButton,
} from "../../../liff/components/Mobile.js";

function ReportsListShell({ title, kind, scopeLabel }: { title: string; kind: "daily" | "weekly" | "monthly"; scopeLabel: string }): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<ReportsListData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<"daily" | "weekly" | "monthly">(kind);

  useEffect(() => {
    if (!session) return;
    setError(null);
    fetchReportsList(session.tenant_id, session.session_token, period)
      .then(setData)
      .catch((e: unknown) => setError((e as Error).message));
  }, [session, period]);

  if (error) return (
    <MobileShell title="無法載入報表列表" subtitle={scopeLabel}>
      <Card><p className="text-sm text-danger">{error}</p></Card>
    </MobileShell>
  );
  if (!data) return <MobileSpinner message="載入報表中" />;
  return (
    <MobileShell title={title} subtitle={scopeLabel}>
      <Card className="!p-2">
        <div className="grid grid-cols-3 gap-1.5">
          {(["daily", "weekly", "monthly"] as const).map((k) => {
            const active = period === k;
            return (
              <button
                key={k}
                onClick={() => setPeriod(k)}
                className={`h-10 rounded-lg text-xs font-medium ${active ? "bg-accent text-text-inverse" : "bg-bg-raised text-text-secondary"}`}
              >
                {k === "daily" ? "日報" : k === "weekly" ? "週報" : "月報"}
              </button>
            );
          })}
        </div>
      </Card>

      <Card>
        <CardHeader title="可下載報表" hint={`${data.reports.length} 份`} />
        {data.reports.length === 0 ? (
          <EmptyMobile
            title="目前沒有可下載的報表"
            body="請稍後再回來查看。"
          />
        ) : (
          <ul className="space-y-2 -my-1">
            {data.reports.map((r) => (
              <li key={r.report_id}>
                <ListRow
                  label={r.title}
                  sublabel={`${r.scope_label} · ${r.date_local} · ${r.status === "ready" ? "可下載" : r.status === "queued" ? "產製中" : "失敗"}`}
                  onClick={() => { window.location.href = `/liff/reports/${encodeURIComponent(r.report_id)}`; }}
                  disabled={r.status !== "ready"}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <SecondaryButton href="/liff/reports">回到全部報表</SecondaryButton>
    </MobileShell>
  );
}

/** /liff/reports/site — manager / tenant_admin scope. */
export function ReportsSitePage(): React.ReactElement {
  return <ReportsListShell title="案場報表" kind="daily" scopeLabel="案場主管視角" />;
}

/** /liff/reports/audit — auditor scope (read-only, PII-redacted). */
export function ReportsAuditPage(): React.ReactElement {
  return <ReportsListShell title="稽核報表" kind="monthly" scopeLabel="稽核員視角 · 唯讀" />;
}
